import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, Loader2, Plus, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

/**
 * ImageLibraryDialog — Zentrale Übersicht aller Produktbilder mit Suche & Filter.
 *
 * Zweck: Statt Bilder mehrfach hochzuladen, kann die Admin bestehende Bilder
 * per Klick anderen Produkten zuweisen. Zeigt bei jedem Bild:
 *   - Wie oft es verwendet wird (Zähler)
 *   - In welchen Produkten (auf Klick sichtbar)
 *   - Button "Zu Produkt hinzufügen" → Dropdown mit Produktauswahl
 */
export default function ImageLibraryDialog({ isOpen, onClose, products = [], onAttach }) {
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [targetProductId, setTargetProductId] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [tab, setTab] = useState('all'); // 'all' or 'orphans'
  const [orphans, setOrphans] = useState(null);
  const [orphanSelection, setOrphanSelection] = useState(new Set());
  const [deletingOrphans, setDeletingOrphans] = useState(false);

  useEffect(() => {
    if (isOpen) loadLibrary();
    else {
      setQuery('');
      setSelectedImage(null);
      setTargetProductId('');
      setTab('all');
      setOrphans(null);
      setOrphanSelection(new Set());
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && tab === 'orphans' && !orphans) loadOrphans();
  }, [tab, isOpen]);

  const loadOrphans = async () => {
    try {
      setLoading(true);
      const r = await axiosInstance.get('/products/admin/orphan-images');
      setOrphans(r.data);
    } catch (e) {
      toast.error('Verwaiste Bilder konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrphanSelection = (id) => {
    setOrphanSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllOrphans = () => {
    if (!orphans) return;
    setOrphanSelection(new Set(orphans.orphans.map(o => o.id)));
  };

  const deleteSelectedOrphans = async () => {
    if (!orphanSelection.size) return;
    const ok = window.confirm(
      `${orphanSelection.size} verwaiste Bild(er) endgültig aus der Bibliothek entfernen?\n\nHinweis: Der Speicherplatz auf dem Server wird dadurch nicht komplett freigegeben, aber sie tauchen nicht mehr in deiner Bibliothek auf.\n\nAktion ist nicht umkehrbar.`
    );
    if (!ok) return;
    try {
      setDeletingOrphans(true);
      const r = await axiosInstance.post('/products/admin/orphan-images/delete', {
        file_ids: Array.from(orphanSelection),
      });
      toast.success(r.data.message);
      setOrphanSelection(new Set());
      await loadOrphans();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeletingOrphans(false);
    }
  };

  const loadLibrary = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/products/admin/image-library');
      setLibrary(r.data);
    } catch (e) {
      toast.error('Bibliothek konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!library) return [];
    if (!query.trim()) return library.images;
    const q = query.toLowerCase();
    return library.images.filter(img => {
      if (img.url.toLowerCase().includes(q)) return true;
      return img.used_by.some(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.sku || '').toLowerCase().includes(q) ||
        (u.category || '').toLowerCase().includes(q)
      );
    });
  }, [library, query]);

  const attachToProduct = async () => {
    if (!selectedImage || !targetProductId) return;
    const alreadyOn = selectedImage.used_by.some(u => u.id === targetProductId);
    if (alreadyOn) {
      toast.info('Dieses Bild ist bereits bei dem gewählten Produkt zugeordnet.');
      return;
    }
    try {
      setAttaching(true);
      const r = await axiosInstance.post('/products/admin/image-library/attach', {
        product_id: targetProductId,
        image_url: selectedImage.url,
      });
      if (r.data.success) {
        toast.success(r.data.message);
        onAttach?.();
        // Refresh library so counters update
        await loadLibrary();
        setTargetProductId('');
      } else {
        toast.info(r.data.message);
      }
    } catch (e) {
      toast.error('Zuweisung fehlgeschlagen');
    } finally {
      setAttaching(false);
    }
  };

  // Ordne Produkte alphabetisch, exkludiere die bereits verknüpften
  const availableProducts = useMemo(() => {
    if (!selectedImage) return [];
    const usedIds = new Set(selectedImage.used_by.map(u => u.id));
    return products
      .filter(p => !usedIds.has(p.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [selectedImage, products]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-5xl rounded-none max-h-[90vh] overflow-y-auto"
        data-testid="image-library-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#5D4E3C] flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Bild-Bibliothek
            {library && (
              <span className="text-xs font-normal text-[#8B7355] ml-auto">
                {library.total_unique} einzigartige Bilder · {library.total_references} Verwendungen
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#8B6F4D]" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tabs */}
            <div className="flex border-b border-[#D4C4B0]">
              <button
                onClick={() => setTab('all')}
                className={`px-4 py-2 text-xs font-semibold transition-colors ${
                  tab === 'all'
                    ? 'text-[#5D4E3C] border-b-2 border-[#8B6F4D] -mb-px'
                    : 'text-[#8B7355] hover:text-[#5D4E3C]'
                }`}
                data-testid="library-tab-all"
              >
                Alle Bilder
              </button>
              <button
                onClick={() => setTab('orphans')}
                className={`px-4 py-2 text-xs font-semibold transition-colors ${
                  tab === 'orphans'
                    ? 'text-[#5D4E3C] border-b-2 border-red-400 -mb-px'
                    : 'text-[#8B7355] hover:text-[#5D4E3C]'
                }`}
                data-testid="library-tab-orphans"
              >
                🧹 Verwaiste Bilder
                {orphans && orphans.count > 0 && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full">
                    {orphans.count}
                  </span>
                )}
              </button>
            </div>

            {tab === 'all' && library && (
              <div className="space-y-4">
                {/* Suche */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Suche in Produktnamen, SKU, Kategorien..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-9 rounded-none border-[#C4A084]"
                    data-testid="library-search"
                  />
                </div>

                {/* Grid */}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto pr-1">
                  {filtered.length === 0 ? (
                    <p className="col-span-full text-sm text-center text-gray-500 py-8">
                      Keine Treffer für &quot;{query}&quot;
                    </p>
                  ) : filtered.map((img, i) => (
                    <button
                      key={img.url}
                      onClick={() => { setSelectedImage(img); setTargetProductId(''); }}
                      className={`relative border overflow-hidden aspect-square group ${
                        selectedImage?.url === img.url
                          ? 'border-[#8B6F4D] border-2 shadow-md'
                          : 'border-[#D4C4B0] hover:border-[#8B6F4D]'
                      }`}
                      data-testid={`library-image-${i}`}
                    >
                      <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-0.5 right-0.5 bg-white/90 text-[#5D4E3C] text-[10px] px-1 font-mono">
                        {img.count}×
                      </div>
                    </button>
                  ))}
                </div>

                {/* Detail-Bereich beim ausgewählten Bild */}
                {selectedImage && (
                  <div className="border border-[#D4C4B0] p-3 bg-[#FAF5EE] space-y-3" data-testid="library-selected-detail">
                    <div className="flex items-start gap-3">
                      <img src={selectedImage.url} alt="" className="w-24 h-24 object-cover border border-[#D4C4B0]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#5D4E3C] font-semibold mb-1">
                          Wird verwendet in {selectedImage.count} Produkt{selectedImage.count === 1 ? '' : 'en'}:
                        </p>
                        <ul className="text-xs text-[#8B7355] max-h-16 overflow-y-auto space-y-0.5">
                          {selectedImage.used_by.map(u => (
                            <li key={u.id} className="truncate">
                              • {u.name} {u.sku ? <span className="text-gray-400">({u.sku})</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <button
                        onClick={() => setSelectedImage(null)}
                        className="text-gray-400 hover:text-red-500"
                        title="Auswahl aufheben"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="border-t border-[#D4C4B0] pt-3">
                      <label className="text-xs font-semibold text-[#5D4E3C] block mb-1.5">
                        ➕ Zu weiterem Produkt hinzufügen:
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={targetProductId}
                          onChange={(e) => setTargetProductId(e.target.value)}
                          className="flex-1 border border-[#D4C4B0] p-2 text-sm bg-white rounded-none focus:outline-none focus:border-[#8B6F4D]"
                          data-testid="library-target-select"
                        >
                          <option value="">— Produkt wählen —</option>
                          {availableProducts.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} {p.sku ? `(${p.sku})` : ''}
                            </option>
                          ))}
                        </select>
                        <Button
                          onClick={attachToProduct}
                          disabled={attaching || !targetProductId}
                          className="rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white"
                          data-testid="library-attach-btn"
                        >
                          {attaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        </Button>
                      </div>
                      {availableProducts.length === 0 && (
                        <p className="text-[11px] text-gray-500 mt-1">
                          Dieses Bild ist bereits allen Produkten zugeordnet.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'orphans' && orphans && (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                  <p><strong>Was sind verwaiste Bilder?</strong></p>
                  <p className="mt-1">
                    Bilder, die auf dem Server gespeichert sind, aber zu keinem Produkt mehr gehören
                    (z. B. weil das Produkt gelöscht oder ein Bild ausgetauscht wurde).
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#5D4E3C] font-semibold">
                    {orphans.count} verwaist · {orphans.total_size_mb} MB
                  </span>
                  <div className="ml-auto flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={selectAllOrphans}
                      disabled={!orphans.orphans.length}
                      className="rounded-none border-[#D4C4B0] text-[#5D4E3C] h-8 text-xs"
                      data-testid="orphans-select-all"
                    >
                      Alle auswählen
                    </Button>
                    <Button
                      size="sm"
                      onClick={deleteSelectedOrphans}
                      disabled={!orphanSelection.size || deletingOrphans}
                      className="rounded-none bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
                      data-testid="orphans-delete-btn"
                    >
                      {deletingOrphans ? <Loader2 className="h-3 w-3 animate-spin" /> : <>🗑 {orphanSelection.size} löschen</>}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto pr-1">
                  {orphans.orphans.length === 0 ? (
                    <p className="col-span-full text-sm text-center text-gray-500 py-8">
                      Keine verwaisten Bilder gefunden — alles sauber! ✨
                    </p>
                  ) : orphans.orphans.map((o, i) => (
                    <button
                      key={o.id}
                      onClick={() => toggleOrphanSelection(o.id)}
                      className={`relative border overflow-hidden aspect-square group text-left ${
                        orphanSelection.has(o.id)
                          ? 'border-red-500 border-2 ring-2 ring-red-200'
                          : 'border-[#D4C4B0] hover:border-red-400'
                      }`}
                      data-testid={`orphan-image-${i}`}
                    >
                      <img src={o.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-0.5 left-0.5 bg-white/90 text-[#5D4E3C] text-[9px] px-1 truncate max-w-[70%]">
                        {o.filename || '?'}
                      </div>
                      {orphanSelection.has(o.id) && (
                        <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                          <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded">✓</span>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1">
                        {(o.size / 1024).toFixed(0)} KB
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
